import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { fetchPastStreams } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Link } from 'react-router-dom';

interface PastVideo {
    id: number;
    title: string;
    date: string;
    thumbnail: string;
    videoUrl: string;
}

const Videos = () => {
    const [activeTab, setActiveTab] = useState<'live' | 'past'>('live');
    const [pastVideos, setPastVideos] = useState<PastVideo[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [youtubeLive, setYoutubeLive] = useState<boolean | null>(null);
    const { toast } = useToast();

    const YOUTUBE_API_KEY = 'AIzaSyDq7dD7vsFOxnllzngx1sH8IlrPAlF1sZU'; // Replace with your actual API key
    const CHANNEL_ID = 'UC-Pspy21gS5ZX54wRRgalaA';

    useEffect(() => {
        if (activeTab === 'past') {
            loadPastVideos();
        }
        if (activeTab === 'live') {
            checkYouTubeLive();
        }
    }, [activeTab]);

    const loadPastVideos = async () => {
        setLoading(true);
        try {
            const response = await fetchPastStreams();
            const data = response.data.videos;

            const formatted: PastVideo[] = data.map((item: any) => ({
                id: item.id,
                title: item.title,
                date: item.created_at,
                thumbnail: item.thumbnail || 'https://via.placeholder.com/640x360?text=No+Thumbnail',
                videoUrl: item.video_url,
            }));

            setPastVideos(formatted);
        } catch (err) {
            console.error(err);
            setError('Failed to load past videos.');
            toast({ title: 'Error', description: 'Unable to fetch past streams', variant: 'destructive' });
        } finally {
            setLoading(false);
        }
    };

    const checkYouTubeLive = async () => {
        try {
            const res = await fetch(
                `https://www.googleapis.com/youtube/v3/search?part=snippet&channelId=${CHANNEL_ID}&type=video&eventType=live&key=${YOUTUBE_API_KEY}`
            );
            const data = await res.json();
            setYoutubeLive(data.items.length > 0);
        } catch (err) {
            console.error('Error checking YouTube live status:', err);
            setYoutubeLive(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-12">
                    <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Video Stream</h1>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        Watch our latest live and past video streams
                    </p>
                </div>

                <div className="flex justify-center space-x-4 mb-8">
                    <button
                        onClick={() => setActiveTab('live')}
                        className={`px-4 py-2 font-semibold rounded ${activeTab === 'live' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                    >
                        🔴 Live Videos
                    </button>
                    <button
                        onClick={() => setActiveTab('past')}
                        className={`px-4 py-2 font-semibold rounded ${activeTab === 'past' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                    >
                        📼 Past Streams
                    </button>
                </div>

                {activeTab === 'live' && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
                        <div className="w-full">
                            <div className="aspect-video bg-white border-2 border-yellow-500 rounded flex flex-col items-center justify-center px-4">
                                <a
                                    href="https://www.facebook.com/dioceseofibadansouth/live_videos"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block w-full max-w-md mx-auto rounded-lg overflow-hidden shadow-md border border-purple-200 hover:shadow-lg transition"
                                >
                                    {/* <img src="/path-to-thumbnail.jpg" alt="Watch Live on Facebook" className="w-full" /> */}
                                    <div className="bg-white py-4 px-6 text-center text-purple-700 font-semibold">
                                        Watch Live on Facebook
                                    </div>
                                </a>

                            </div>
                            <h3 className="text-center font-medium text-gray-800 mt-2">Facebook Live</h3>
                        </div>

                        <div className="w-full">
                            {youtubeLive === null ? (
                                <div className="aspect-video bg-gray-100 flex items-center justify-center">
                                    <p className="text-gray-600">Checking YouTube Live...</p>
                                </div>
                            ) : youtubeLive ? (
                                <div className="aspect-video">
                                    <iframe
                                        title="YouTube Live"
                                        src={`https://www.youtube.com/embed/live_stream?channel=${CHANNEL_ID}&autoplay=1`}
                                        allow="autoplay; encrypted-media"
                                        allowFullScreen
                                        width="100%"
                                        height="300"
                                        frameBorder="0"
                                    ></iframe>
                                </div>
                            ) : (
                                <div className="aspect-video bg-white border-2 border-yellow-500 rounded flex flex-col items-center justify-center px-4">
                                    <h4 className="text-lg font-semibold text-purple-800">We’re not live on YouTube right now</h4>
                                    <p className="text-sm text-gray-600">
                                        Visit our <a
                                            href="https://www.youtube.com/@DioceseOfIbadanSouth"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 underline"
                                        >YouTube channel</a> for past videos.
                                    </p>
                                </div>
                            )}
                            <h3 className="text-center font-medium text-gray-800 mt-2">YouTube Live</h3>
                        </div>

                        <div className="w-full">
                            <div className="aspect-video bg-white border-2 border-yellow-500 rounded flex flex-col items-center justify-center px-4">
                                <a
                                    href="https://www.tiktok.com/@ibadansouthangdiocese"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 underline"
                                >
                                    Watch on TikTok
                                </a>
                            </div>
                            <h3 className="text-center font-medium text-gray-800 mt-2">TikTok Live</h3>
                        </div>
                    </div>
                )}

                {activeTab === 'past' && (
                    <>
                        <h2 className="text-2xl font-semibold text-gray-800 mb-6">Past Broadcasts</h2>
                        {loading ? (
                            <div className="text-center text-gray-500 animate-pulse">Loading videos...</div>
                        ) : error ? (
                            <div className="text-center text-red-500">{error}</div>
                        ) : pastVideos.length === 0 ? (
                            <div className="text-center py-12">
                                <div className="text-6xl mb-4">🎥</div>
                                <h3 className="text-xl font-medium text-gray-900 mb-2">No videos yet</h3>
                                <p className="text-gray-600">Check back soon for our latest streams.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {pastVideos.map((video) => (
                                    <Card key={video.id} className="border-none shadow-md hover:shadow-xl transition duration-300 group">
                                        <CardContent className="p-0">
                                            <div className="w-full h-64 bg-black flex justify-center items-center">
                                                <iframe
                                                    src={video.videoUrl}
                                                    title={video.title || 'Embedded Video'}
                                                    width="100%"
                                                    height="100%"
                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                    allowFullScreen
                                                    style={{ border: 0 }}
                                                />
                                            </div>
                                            <div className="p-5">
                                                <h3 className="text-lg font-semibold text-gray-800 group-hover:text-blue-600 transition-colors">
                                                    {video.title}
                                                </h3>
                                                <p className="text-sm text-gray-500">
                                                    {new Date(video.date).toLocaleDateString('en-US', {
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric',
                                                    })}
                                                </p>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default Videos;